import { AffiliatePromotionLink } from '@/@types/AffiliatePromotionLink';
import { Checkout } from '@/@types/Checkouts/list-checkouts';
import { Coupon } from '@/@types/Coupons';
import { OfferData } from '@/@types/Products/Offers';
import {
  convertCouponAmount,
  couponCurrencyFromApi,
  normalizeCouponCurrency,
} from './couponScope';

/** Preço mínimo de uma oferta, em centavos: abaixo dele o checkout não aplica o cupom. */
export const COUPON_MIN_FINAL_PRICE = 500;

/** O que os links do cupom usam de uma oferta. */
export interface CouponOffer {
  id: string;
  name: string;
  price: number;
  currency: string | null | undefined;
  is_active?: boolean;
  price_formatted?: string;
}

export function toCouponOffer(offer: OfferData): CouponOffer {
  return {
    id: String(offer.id),
    name: offer.name,
    price: offer.price,
    currency: offer.currency,
    is_active: offer.is_active,
    price_formatted: offer.price_formatted,
  };
}

/**
 * Link do checkout com o cupom já aplicado: a mesma URL do checkout, com
 * `?coupon=CODIGO` e sem parâmetros vazios (ex.: `ref=` sem afiliado).
 * Devolve `null` quando o checkout não tem um link válido.
 */
export function buildCouponLink(
  checkoutLink: string | null | undefined,
  code: string,
): string | null {
  if (!checkoutLink) {
    return null;
  }

  try {
    const url = new URL(checkoutLink);

    Array.from(url.searchParams.entries())
      .filter(([, value]) => value === '')
      .forEach(([key]) => url.searchParams.delete(key));

    url.searchParams.set('coupon', code);

    return url.toString();
  } catch {
    return null;
  }
}

/** Cupom inativo ou vencido não gera link. */
export function isCouponLinkable(coupon: Coupon, now: Date = new Date()): boolean {
  if (coupon.status !== 'active') {
    return false;
  }

  return !coupon.expiresAt || new Date(coupon.expiresAt) >= now;
}

/**
 * Valor fixo só se aplica se o preço final da oferta não cair abaixo do
 * mínimo. Cupom em outra moeda é convertido pela cotação; sem cotação, a
 * oferta entra e o checkout decide.
 */
export function isCouponApplicableToOffer(
  coupon: Coupon,
  offer: Pick<CouponOffer, 'price' | 'currency'>,
  usdToBrl?: number,
): boolean {
  if (coupon.discountType !== 'fixed') {
    return true;
  }

  const offerCurrency = normalizeCouponCurrency(offer.currency);
  const couponCurrency = couponCurrencyFromApi(coupon.currency) ?? offerCurrency;
  const amount = coupon.discountAmount ?? 0;

  if (couponCurrency !== offerCurrency && !usdToBrl) {
    return true;
  }

  const discount =
    couponCurrency === offerCurrency
      ? amount
      : convertCouponAmount(amount, couponCurrency, usdToBrl as number);

  return Number(offer.price) - discount >= COUPON_MIN_FINAL_PRICE;
}

/**
 * Um link por checkout ativo das ofertas em que o cupom vale: as marcadas no
 * cupom (nenhuma marcada = todas), ativas e, no valor fixo, só as em que o
 * desconto se aplica.
 */
export function buildCouponLinks(
  coupon: Coupon,
  checkouts: Checkout[],
  offers: CouponOffer[],
  usdToBrl?: number,
): AffiliatePromotionLink[] {
  const offerIds = (coupon.offerIds ?? []).map(String);

  return checkouts.flatMap((checkout) => {
    const offerId = String(checkout.offer?.id);

    if (!checkout.is_active) {
      return [];
    }

    if (offerIds.length > 0 && !offerIds.includes(offerId)) {
      return [];
    }

    const offer =
      offers.find((item) => item.id === offerId) ??
      (checkout.offer ? toCouponOffer(checkout.offer) : undefined);

    if (
      !offer ||
      offer.is_active === false ||
      !isCouponApplicableToOffer(coupon, offer, usdToBrl)
    ) {
      return [];
    }

    const url = buildCouponLink(checkout.link, coupon.name);

    if (!url) {
      return [];
    }

    return [
      {
        id: String(checkout.id),
        url,
        checkoutName: checkout.name,
        offerName: offer.name,
        offerPriceFormatted: offer.price_formatted,
      },
    ];
  });
}
